import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Content } from '@/components/ui/content';
import { ContentLoader } from '@/components/ui/loaders';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { isSaleEditable } from '@/lib/sale-status';
import { useDynamicDetail, useDynamicList } from '@/services/dynamic-resource';
import type { DraftItem } from '../+components/item-form';
import { SaleWizard } from '../+components/sale-wizard';

export const Route = createFileRoute('/_app/sales/$id/')({
  head: () => meta({ label: 'Editar Pedido' }),
  component: RouteComponent,
});

function toDraftItem(item: any): DraftItem {
  return {
    key: item.id,
    shirtTypeId: item.shirtTypeId,
    shirtTypeLabel: item.shirtType?.description ?? '',
    shirtTypePrice: item.shirtTypePrice,
    sleeveTypeId: item.sleeveTypeId,
    sleeveTypeLabel: item.sleeveType?.description ?? '',
    sleeveTypePrice: item.sleeveTypePrice,
    collarTypeId: item.collarTypeId,
    collarTypeLabel: item.collarType?.description ?? '',
    collarTypePrice: item.collarTypePrice,
    fabricTypeId: item.fabricTypeId,
    fabricTypeLabel: item.fabricType?.description ?? '',
    fabricTypePrice: item.fabricTypePrice,
    printTypeId: item.printTypeId,
    printTypeLabel: item.printType?.description ?? '',
    printTypePrice: item.printTypePrice,
    sizeBreakdown: item.sizeBreakdown?.length
      ? item.sizeBreakdown
      : item.shirtSizeId
        ? [
            {
              shirtSizeId: item.shirtSizeId,
              label: item.shirtSize?.description ?? '',
              quantity: item.quantity,
            },
          ]
        : [],
    quantity: item.quantity,
    selectedOptions: item.selectedOptions ?? [],
    artworkPath: item.artworkPath ?? undefined,
    unitaryPrice: item.unitaryPrice,
    totalPrice: item.totalPrice,
  };
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { data: sale, isLoading, isError, refetch } = useDynamicDetail('sales', id);
  const { data: itemsResponse } = useDynamicList('sale-items', {
    saleId: id,
    perPage: 100,
  });

  if (isError) {
    return (
      <Content
        breadcrumbs={{
          title: 'Editar Pedido',
          items: [{ label: 'Listagem', route: '/sales' }],
        }}
      >
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">Não foi possível carregar este pedido.</p>
          <button
            type="button"
            className="text-primary underline"
            onClick={() => refetch()}
          >
            Tentar novamente
          </button>
        </div>
      </Content>
    );
  }

  if (!sale || !itemsResponse) return <ContentLoader show={isLoading} />;

  if (!isSaleEditable(sale.status)) {
    return <Navigate to="/sales/$id/view" params={{ id }} />;
  }

  const items = (itemsResponse.data ?? []).filter((item: any) => item.saleId === id);

  return (
    <SaleWizard
      mode="edit"
      saleId={id}
      saleNumber={sale.saleNumber ?? `#${sale.id.slice(0, 8)}`}
      initialCustomer={sale.customer}
      initialValues={{
        customerId: sale.customerId,
        paymentMethodId: sale.paymentMethodId,
        companyId: sale.companyId,
        status: sale.status,
        deliveryDate: date(sale.deliveryDate).format('YYYY-MM-DD'),
        depositType: sale.depositType,
        depositValue: sale.depositValue,
      }}
      initialItems={items.map(toDraftItem)}
      initialSaleOptions={sale.selectedOptions ?? []}
    />
  );
}
